package top.wcpe.allincore.ui.runtime.style

import top.wcpe.allincore.layout.engine.LayoutStyle
import kotlin.jvm.JvmStatic

/**
 * UI 元素的视觉 + 布局样式聚合。
 *
 * 聚合一份 [LayoutStyle] 与视觉 modifier 链兼容输入（颜色、旧 [StyleVisual] 等）。
 *
 * 批4b-2 曾因视觉段依赖 platform-client-api 渲染类型而拆成 expect/actual；
 * ADR-0072 R3 视觉件（[StyleVisual] / [VisualModifierChain]）换用 `render.model` 镜像类型
 * 迁 commonMain 后，expect/actual 收敛回单一 common 类（原 JVM 侧 Consumer 风格
 * mutator 按批4b-1 先例换 Kotlin 函数类型，Kotlin 调用点零改动）。
 */
class Style {
    private val layoutStyle: LayoutStyle = LayoutStyle.create()
    private val visualStyle: StyleVisual = StyleVisual()
    private val explicitEffectModifiers: MutableList<VisualModifier> = mutableListOf()

    private var bgColor: Int = 0
    private var fgColor: Int = 0xFFFFFFFF.toInt()
    private var modifierRevision: Int = 0
    private var cachedVisualFingerprint: Int = Int.MIN_VALUE
    private var cachedModifierRevision: Int = Int.MIN_VALUE
    private var cachedModifierBackground: Int = 0
    private var cachedModifierChain: VisualModifierChain? = null

    /** 背景色，ARGB，默认 0 表示透明。 */
    fun backgroundColor(): Int = bgColor

    /** 背景色，ARGB 链式 setter。 */
    fun backgroundColor(value: Int): Style {
        if (bgColor != value) {
            bgColor = value
            modifierRevision++
        }
        return this
    }

    /** 前景/文本色，ARGB，默认白色不透明。 */
    fun foregroundColor(): Int = fgColor

    /** 前景/文本色，ARGB 链式 setter。 */
    fun foregroundColor(value: Int): Style {
        fgColor = value
        return this
    }

    /** 获取内嵌布局样式（只读引用，内容可变）。 */
    fun layout(): LayoutStyle = layoutStyle

    /** 链式调用：临时获取 [LayoutStyle] 进行变更。 */
    fun layout(mutator: (LayoutStyle) -> Unit): Style {
        mutator(layoutStyle)
        return this
    }

    /** 获取视觉子段（只读引用，内容可变）。 */
    fun visual(): StyleVisual = visualStyle

    /** 链式调用：临时获取 [StyleVisual] 进行变更。 */
    fun visual(mutator: (StyleVisual) -> Unit): Style {
        mutator(visualStyle)
        return this
    }

    /** 添加 IR modifier chain 中的显式 effect modifier，保持 DSL 声明顺序。 */
    fun addEffectModifier(modifier: VisualModifier): Style {
        explicitEffectModifiers += modifier
        modifierRevision++
        return this
    }

    /** 获取当前视觉 modifier 链；稳定样式跨帧复用同一不可变链。 */
    fun modifiers(): VisualModifierChain {
        val visualFingerprint = visualStyle.modifierFingerprint()
        cachedModifierChain?.takeIf {
            cachedVisualFingerprint == visualFingerprint &&
                cachedModifierRevision == modifierRevision &&
                cachedModifierBackground == bgColor
        }?.let { return it }

        val base = VisualModifierChain.fromVisual(visualStyle, bgColor)
        val resolved = if (explicitEffectModifiers.isEmpty()) {
            base
        } else {
            // historyLayer 是真作用域型效果（FR-UI-023 self-only 隔离须把元素自身绘制重定向到离屏靶，
            // 见 ADR-0066 §7 / ADR-0047）：从 effect 链分出转为 scoped History 括号，避免被当作逐图元材质点调用双绘。
            // particleLayer（FR-UI-031 / ADR-0071）是独立段：分出到 particleLayers，由 UIRenderer 按 front/back 注入。
            val history = explicitEffectModifiers.filterIsInstance<VisualModifier.HistoryLayer>()
            val particles = explicitEffectModifiers.filterIsInstance<VisualModifier.ParticleLayer>()
            val others = explicitEffectModifiers.filterNot {
                it is VisualModifier.HistoryLayer || it is VisualModifier.ParticleLayer
            }
            base.copy(
                effectPaint = base.effectPaint + others,
                scoped = if (history.isEmpty()) {
                    base.scoped
                } else {
                    base.scoped +
                        history.map { ScopedVisualModifier.History(it) }
                },
                particleLayers = base.particleLayers + particles,
            )
        }
        cachedVisualFingerprint = visualFingerprint
        cachedModifierRevision = modifierRevision
        cachedModifierBackground = bgColor
        cachedModifierChain = resolved
        return resolved
    }

    /**
     * 将 [other] 的字段拷贝到当前 Style，供 UIElement.style 使用。
     * 注意：拷贝是浅复制字段值，[LayoutStyle] 内部为可变值类型，按字段逐项复制。
     */
    fun copyFrom(other: Style) {
        bgColor = other.bgColor
        fgColor = other.fgColor
        explicitEffectModifiers.clear()
        explicitEffectModifiers.addAll(other.explicitEffectModifiers)
        visualStyle.copyFrom(other.visualStyle)
        modifierRevision++
        cachedModifierChain = null
        copyLayoutFields(layoutStyle, other.layoutStyle)
    }

    companion object {
        @JvmStatic
        fun create(): Style = Style()
    }
}

/**
 * 布局段逐字段拷贝：[Style.copyFrom] 使用，集中一处避免复制清单散落。
 */
internal fun copyLayoutFields(target: LayoutStyle, source: LayoutStyle) {
    target.mode = source.mode
    target.width = source.width
    target.height = source.height
    target.minWidth = source.minWidth
    target.minHeight = source.minHeight
    target.maxWidth = source.maxWidth
    target.maxHeight = source.maxHeight
    target.margin = source.margin
    target.padding = source.padding
    target.gap = source.gap
    target.align = source.align
    target.justify = source.justify
    target.flexGrow = source.flexGrow
    target.flexShrink = source.flexShrink
    target.flexBasis = source.flexBasis
}
